"use client";

import { GripHorizontal, MoveDiagonal2 } from "lucide-react";
import { useCallback, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

import type { DashboardWidgetDto } from "@/lib/api/types";

import { useDashboardBuilderOptional } from "@/contexts/DashboardBuilderContext";
import { useMediaQuery } from "@/hooks/use-media-query";

import {
  clampLayoutRect,
  GRID_COLUMNS,
  MAX_ROW_SPAN,
  mergeLayout,
  type LayoutRect,
} from "./dashboard-layout-utils";
import { WidgetRenderer } from "./WidgetRenderer";

const ROW_SNAP_PX = 112;

type GridItemProps = {
  display: DashboardWidgetDto;
  desktop: boolean;
  gridRef: React.RefObject<HTMLDivElement | null>;
};

function DashboardGridItem({ display, desktop, gridRef }: GridItemProps) {
  const builder = useDashboardBuilderOptional();
  const editMode = Boolean(builder?.editMode);
  const selected = builder?.selectedWidgetId === display.id;

  const layout = useMemo(() => mergeLayout(display, null), [display]);

  const [dragKind, setDragKind] = useState<"move" | "resize" | null>(null);
  const dragRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    origin: LayoutRect;
  } | null>(null);

  const colSpan = Math.min(Math.max(layout.width, 1), GRID_COLUMNS);
  const rowSpan = Math.min(Math.max(layout.height, 1), MAX_ROW_SPAN);
  const colStart = Math.max(1, Math.min(layout.position_x + 1, GRID_COLUMNS - colSpan + 1));
  const rowStart = Math.max(1, layout.position_y + 1);
  const order = layout.position_y * 100 + layout.position_x;

  const flushDrag = useCallback(
    (ev: ReactPointerEvent, kind: "move" | "resize") => {
      if (!builder || !gridRef.current || !dragRef.current) {
        return;
      }
      const grid = gridRef.current;
      const colW = Math.max(1, grid.clientWidth / GRID_COLUMNS);
      const dx = ev.clientX - dragRef.current.startClientX;
      const dy = ev.clientY - dragRef.current.startClientY;
      const o = dragRef.current.origin;

      if (kind === "move") {
        const dCol = Math.round(dx / colW);
        const dRow = Math.round(dy / ROW_SNAP_PX);
        builder.commitLayout(display.id, {
          position_x: o.position_x + dCol,
          position_y: o.position_y + dRow,
          width: o.width,
          height: o.height,
        });
      } else {
        const dCol = Math.round(dx / colW);
        const dRow = Math.round(dy / ROW_SNAP_PX);
        builder.commitLayout(display.id, {
          position_x: o.position_x,
          position_y: o.position_y,
          width: o.width + dCol,
          height: o.height + dRow,
        });
      }
    },
    [builder, display.id, gridRef],
  );

  const onPointerMove = useCallback(
    (ev: ReactPointerEvent) => {
      if (!dragKind || !dragRef.current || ev.pointerId !== dragRef.current.pointerId) {
        return;
      }
      flushDrag(ev, dragKind);
    },
    [dragKind, flushDrag],
  );

  const onPointerUp = useCallback(
    (ev: ReactPointerEvent) => {
      if (!dragKind || !dragRef.current || ev.pointerId !== dragRef.current.pointerId) {
        return;
      }
      try {
        flushDrag(ev, dragKind);
      } finally {
        setDragKind(null);
        dragRef.current = null;
      }
    },
    [dragKind, flushDrag],
  );

  const releaseDrag = useCallback(() => {
    setDragKind(null);
    dragRef.current = null;
  }, []);

  const onPointerCancel = useCallback((ev: ReactPointerEvent) => {
    if (!dragRef.current || ev.pointerId !== dragRef.current.pointerId) {
      return;
    }
    releaseDrag();
  }, [releaseDrag]);

  return (
    <div
      className={`group/widget relative min-h-0 min-w-0 ${editMode && selected ? "z-[2]" : ""} ${
        editMode && selected ? "ring-2 ring-primary/35 ring-offset-2 ring-offset-background rounded-2xl" : ""
      }`}
      style={
        desktop
          ? {
              gridColumn: `${colStart} / span ${colSpan}`,
              gridRow: `${rowStart} / span ${rowSpan}`,
            }
          : {
              gridColumn: "1 / -1",
              gridRow: "auto",
              order,
            }
      }
    >
      {editMode ? (
        <>
          <div
            className="absolute left-ds-2 right-ds-2 top-0 z-[3] flex h-8 cursor-grab touch-none items-center justify-center rounded-t-lg border border-border/50 bg-card/90 opacity-0 shadow-card backdrop-blur-md transition-opacity hover:opacity-100 active:cursor-grabbing group-hover/widget:opacity-100"
            onPointerDown={(e) => {
              if (e.button !== 0) {
                return;
              }
              e.stopPropagation();
              (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
              dragRef.current = {
                pointerId: e.pointerId,
                startClientX: e.clientX,
                startClientY: e.clientY,
                origin: clampLayoutRect(mergeLayout(display, null)),
              };
              setDragKind("move");
            }}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            onLostPointerCapture={releaseDrag}
            aria-label="Drag widget"
            role="separator"
          >
            <GripHorizontal className="h-4 w-4 text-muted" aria-hidden />
          </div>
          <div
            className={`absolute bottom-2 right-2 z-[3] flex cursor-se-resize touch-none items-center gap-1 rounded-full border border-border/60 bg-card/95 px-2 py-1 text-[11px] font-medium text-muted shadow-card transition-opacity ${
              selected ? "opacity-100" : "opacity-75 hover:opacity-100 group-hover/widget:opacity-100"
            }`}
            onPointerDown={(e) => {
              if (e.button !== 0) {
                return;
              }
              e.stopPropagation();
              (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
              dragRef.current = {
                pointerId: e.pointerId,
                startClientX: e.clientX,
                startClientY: e.clientY,
                origin: clampLayoutRect(mergeLayout(display, null)),
              };
              setDragKind("resize");
            }}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            onLostPointerCapture={releaseDrag}
            aria-label="Resize widget"
            title="Resize widget"
          >
            <MoveDiagonal2 className="h-3 w-3" aria-hidden />
            <span>Resize</span>
          </div>
        </>
      ) : null}
      <div className={editMode ? "flex h-full min-h-0 flex-col pt-7" : "flex h-full min-h-0 flex-col"}>
        <WidgetRenderer widget={display} />
      </div>
    </div>
  );
}

type DashboardGridProps = {
  widgets: DashboardWidgetDto[];
};

export function DashboardGrid({ widgets }: DashboardGridProps) {
  const builder = useDashboardBuilderOptional();
  const desktop = useMediaQuery("(min-width: 768px)");
  const gridRef = useRef<HTMLDivElement>(null);

  const pairs = useMemo(() => {
    return widgets.map((base) => ({
      base,
      display: builder ? builder.getDisplayWidget(base) : base,
    }));
  }, [widgets, builder]);

  return (
    <div
      ref={gridRef}
      className="grid auto-rows-min grid-cols-1 gap-ds-4 md:grid-cols-6"
      style={{ contain: "layout style" }}
    >
      {pairs.map(({ base, display }) => (
        <DashboardGridItem key={base.id} display={display} desktop={desktop} gridRef={gridRef} />
      ))}
    </div>
  );
}
